/**
 * Drives the D-1 intermediate "show me where I stand" photo flow.
 *
 * Reuses the same camera screen as the standard activity submission
 * (route `camera` → `CameraComponent.web.js` on web, `CameraComponent.js` on
 * native) so we get a working capture flow on every platform without
 * duplicating webcam logic. Photos taken with our dedicated `SAM_WORK_CHECK`
 * formId land in `state.camera.lastPhoto`; this hook watches that slot,
 * uploads the file as multipart to `/sam-work-check`, then clears it.
 *
 * Déplacé de `student/sam-sessions/hooks/`, ses trois lectures du legacy levées, à comportement
 * égal : l'appareil photo arrive en paramètre (`launchCamera`, l'écran le tient : `useCamera` du
 * legacy, `useMediaPickerAdapter` des ports) ; la réponse se lit par `onResult` d'`apiRequest` au lieu
 * de `useApiStatus` (mêmes bulles, même remise au repos) ; le web se lit sur `Platform`.
 */
import * as amplitude from "@amplitude/analytics-react-native"
import { useCallback, useEffect } from "react"
import { Platform } from "react-native"
import { useDispatch, useSelector } from "react-redux"

import { formIds } from "../../../data/constants/formConstants"
import { SAM_COMPLETION_LEVEL } from "../../../data/constants/samConstants"
import { apiIdle, apiRequest } from "../../../data/redux/actions/api"
import { clearLastPhoto } from "../../../data/redux/actions/camera"
import { injectSamBubble, injectStudentScan } from "../../../data/redux/actions/data"
import { samService } from "../../../data/services/samService"
import { fetchResourceFromURI } from "../../../data/utility/fileFromURI"
import { requestOptionsFor } from "../../shared/apiCall"

type CameraPhoto = { uri?: string; fileName?: string; mimeType?: string; type?: string }

type WorkCheckRoot = {
    camera?: { lastPhoto?: CameraPhoto | null; formId?: string | null }
    api?: { requestSamWorkCheck?: { request?: boolean } }
}

type WorkCheckResult = {
    ok: boolean
    data?: { completion_level?: string; key_gaps?: string[] } | null
    error?: { message?: string; error?: string } | null
}

export function useSamWorkCheck(
    activityId: number | null,
    onSent: (() => void) | undefined,
    launchCamera: () => void,
    options: { offlineDialog?: boolean } = {}
) {
    const offlineDialog = options.offlineDialog ?? true
    const dispatch = useDispatch()

    const lastPhoto = useSelector((state: WorkCheckRoot) => state.camera?.lastPhoto)
    const lastPhotoFormId = useSelector((state: WorkCheckRoot) => state.camera?.formId)
    const isUploading = useSelector((state: WorkCheckRoot) => state.api?.requestSamWorkCheck?.request === true)

    const onResult = useCallback(
        (result: WorkCheckResult) => {
            if (!activityId) return
            if (result.ok) {
                // `useApiStatus` remettait la tranche au repos à chaque succès.
                dispatch(apiIdle(samService.requestSamWorkCheck))
                const data = result.data
                if (!data) return
                const bubble = buildWorkCheckBubble(data.completion_level, data.key_gaps || [])
                dispatch(injectSamBubble(activityId, bubble, "encouraging"))
                amplitude.track("sam.work_check.qualified", {
                    activity_id: activityId,
                    completion_level: data.completion_level,
                    key_gaps_count: (data.key_gaps || []).length,
                })
                return
            }
            // `useApiStatus` ne lisait qu'un échec qui porte un message (les autres passent par
            // l'intercepteur global de l'app).
            const error = result.error
            if (!error?.message) return
            // Mode dégradé : l'élève doit voir que sa photo est arrivée et
            // que c'est Sam qui n'a pas pu la lire — sinon le bouton semble
            // ne rien faire du tout.
            dispatch(
                injectSamBubble(
                    activityId,
                    error.message ||
                        "Je n'arrive pas à regarder ton travail pour le moment — réessaie dans un instant.",
                    "patient"
                )
            )
            amplitude.track("sam.work_check.failed", {
                activity_id: activityId,
                reason: error.error || "unknown",
            })
            // Sinon un second échec ne redéclenche rien.
            dispatch(apiIdle(samService.requestSamWorkCheck))
        },
        [activityId, dispatch]
    )

    const uploadPhoto = useCallback(
        async (asset: CameraPhoto) => {
            if (!activityId || !asset?.uri) return
            const filename = asset.fileName || `sam_work_check_${Date.now()}.jpg`
            const mimeType = asset.mimeType || asset.type || "image/jpeg"
            const formData = new FormData()
            // L'écho du cliché dans la conversation. Sur web, l'adresse d'un
            // objet sur le Blob de l'envoi plutôt que la data URL en pleine
            // résolution, qui restait plusieurs Mo dans le store.
            let echoUri = asset.uri

            if (Platform.OS === "web") {
                // Sur web la caméra rend une data URL (canvas.toDataURL) et le
                // FormData du navigateur n'accepte qu'un Blob : lui passer la
                // forme React Native {uri,name,type} le sérialise en
                // "[object Object]", que la règle `file` du backend refuse
                // ("Les données sont invalides").
                try {
                    const blob: Blob = await fetchResourceFromURI(asset.uri)
                    formData.append("work_attachment", blob, filename)
                    echoUri = URL.createObjectURL(blob)
                } catch {
                    // L'échec se dit dans la voix de Sam : la photo n'a pas pu être relue.
                    dispatch(
                        injectSamBubble(
                            activityId,
                            "Je n'ai pas réussi à récupérer ta photo — tu peux la reprendre ?",
                            "patient"
                        )
                    )
                    return
                }
            } else {
                formData.append("work_attachment", {
                    uri: asset.uri,
                    name: filename,
                    type: mimeType,
                } as unknown as Blob)
            }

            // Écho immédiat du cliché, avant l'envoi : c'est le seul retour qui
            // prouve à l'élève que sa photo est partie, et il tient même si le
            // quali échoue derrière.
            dispatch(injectStudentScan(activityId, echoUri))

            amplitude.track("sam.work_check.requested", {
                activity_id: activityId,
                source: "camera",
            })

            dispatch(
                apiRequest(samService.requestSamWorkCheck, { activityId }, formData, {}, null, null, {
                    ...requestOptionsFor(offlineDialog),
                    onResult,
                }) as never
            )

            if (onSent) onSent()
        },
        [activityId, dispatch, onSent, offlineDialog, onResult]
    )

    // Watch the camera screen for a fresh photo flagged with our
    // formId, upload it, then clear it so we don't re-trigger on
    // subsequent renders.
    useEffect(() => {
        if (!lastPhoto || lastPhotoFormId !== formIds.SAM_WORK_CHECK) return
        void uploadPhoto(lastPhoto)
        dispatch(clearLastPhoto())
    }, [lastPhoto, lastPhotoFormId, uploadPhoto, dispatch])

    return {
        isUploading,
        pickFromCamera: launchCamera,
    }
}

function buildWorkCheckBubble(level: string | undefined, keyGaps: string[]) {
    const firstGap = keyGaps[0]
    switch (level) {
        case SAM_COMPLETION_LEVEL.unreadable:
            // Le problème est la photo, pas le travail. Le dire, et dire
            // comment le corriger : un élève qui cadre mal a le plus souvent
            // bien travaillé, et s'entendre répondre « tu n'as pas démarré »
            // est à la fois faux et décourageant.
            return firstGap
                ? `${firstGap} Tu peux la reprendre ? Feuille bien à plat, cadrée entière, sans ombre.`
                : "Je n'arrive pas à lire ton travail sur cette photo. Tu peux la reprendre ? Feuille bien à plat, cadrée entière, sans ombre."
        case SAM_COMPLETION_LEVEL.barelyStarted:
            // La lacune, quand il y en a une, porte la RAISON — « cette page ne
            // correspond pas à l'exercice demandé », typiquement, quand l'élève
            // s'est trompé de feuille. La jeter pour servir un message
            // générique lui retirait le seul renseignement utile, et
            // l'accusait au passage de ne pas avoir travaillé.
            return firstGap
                ? `${firstGap} Tu veux reprendre la photo, ou me dire où tu en es ?`
                : "On dirait que tu n'as pas encore vraiment démarré. Pas grave — on attaque ensemble ? Dis-moi ce que tu comprends de l'énoncé."
        case SAM_COMPLETION_LEVEL.advanced:
            return "Tu as vraiment bien avancé ! Quand tu te sens prêt·e, valide l'activité pour que je te prépare une correction détaillée."
        case SAM_COMPLETION_LEVEL.partial:
        default:
            return firstGap
                ? `Bien parti·e ! J'ai noté un point à creuser : « ${firstGap} ». Tu veux qu'on en parle ?`
                : "Bien parti·e. Tu veux qu'on revoie un point ensemble avant de finaliser ?"
    }
}
